// SuccessDialog.tsx — ported from SK-Catalog (src/components/successDialog.tsx),
// adapted to SK-Tickets tokens and the local SVG `Icon`. The "done ✓" moment
// shown AFTER an action completes (positive counterpart to ConfirmDialog).
import React from 'react';
import {
  Modal, View, Text, Pressable, StyleSheet, Dimensions, Platform, TouchableOpacity,
} from 'react-native';
import { Icon } from './Icon';
import { Colors, FontWeight, FontFamily, Radii } from '../theme/tokens';

const { width } = Dimensions.get('window');

type DialogAction = Readonly<{ label: string; onPress: () => void }>;

type SuccessDialogProps = Readonly<{
  visible: boolean;             // Controls visibility
  title?: string;              // Primary heading
  message?: string;            // Supporting line under the title
  summary?: string[];          // Optional bullet lines (e.g. "Ticket #6 removed")
  tone?: 'success' | 'warning';// success (green) | warning (amber, partial result)
  primaryAction?: DialogAction;   // Emphasised action (right)
  secondaryAction?: DialogAction; // Secondary action (left)
  onClose?: () => void;        // Top-right close icon + backdrop dismiss
}>;

export default function SuccessDialog({
  visible,
  title = 'All done',
  message,
  summary,
  tone = 'success',
  primaryAction,
  secondaryAction,
  onClose,
}: SuccessDialogProps) {
  const TONES = {
    success: { accent: Colors.success, soft: Colors.successSoft, icon: 'CheckCircle' as const },
    warning: { accent: Colors.warn, soft: Colors.warnSoft, icon: 'Alert' as const },
  };
  const t = TONES[tone];

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.overlay} onPress={onClose}>
        {/* Stop propagation so taps inside the card don't dismiss it. */}
        <Pressable style={styles.box} onPress={() => {}}>
          <View style={[styles.indicator, { backgroundColor: t.accent }]} />

          {onClose && (
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <Icon name="X" size={18} color={Colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            <View style={[styles.badge, { backgroundColor: t.soft }]}>
              <Icon name={t.icon} size={30} color={t.accent} />
            </View>

            <Text style={styles.title}>{title}</Text>
            {!!message && <Text style={styles.message}>{message}</Text>}

            {!!summary?.length && (
              <View style={styles.summary}>
                {summary.map((line, i) => (
                  <View key={i} style={styles.summaryRow}>
                    <Icon name="Check" size={14} color={t.accent} />
                    <Text style={styles.summaryText}>{line}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {(primaryAction || secondaryAction) && (
            <View style={styles.footer}>
              {secondaryAction && (
                <Pressable
                  style={({ pressed }) => [styles.btn, styles.secondaryBtn, pressed && styles.btnPressed]}
                  onPress={secondaryAction.onPress}
                >
                  <Text style={styles.secondaryText}>{secondaryAction.label}</Text>
                </Pressable>
              )}
              {primaryAction && (
                <Pressable
                  style={({ pressed }) => [styles.btn, { backgroundColor: t.accent }, pressed && styles.btnPressed]}
                  onPress={primaryAction.onPress}
                >
                  <Text style={styles.primaryText}>{primaryAction.label}</Text>
                </Pressable>
              )}
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  box: {
    width: Math.min(width * 0.85, 360),
    backgroundColor: Colors.card,
    borderRadius: Radii.r5,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 8px 16px rgba(0,0,0,0.18)' } as any,
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.18, shadowRadius: 16 },
      android: { elevation: 10 },
    }),
  },
  indicator: { height: 4, width: '100%', opacity: 0.9 },
  content: { paddingTop: 24, paddingHorizontal: 20, paddingBottom: 18, alignItems: 'center' },
  badge: {
    width: 60, height: 60, borderRadius: Radii.full,
    justifyContent: 'center', alignItems: 'center', marginBottom: 14,
  },
  title: {
    fontSize: 18, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans,
    color: Colors.ink, textAlign: 'center', marginBottom: 6, letterSpacing: -0.3,
  },
  message: {
    fontSize: 13.5, fontFamily: FontFamily.sans, color: Colors.ink3,
    textAlign: 'center', lineHeight: 20,
  },
  summary: {
    alignSelf: 'stretch', marginTop: 16, gap: 8,
    borderRadius: Radii.r4, padding: 12, backgroundColor: Colors.paper2,
  },
  summaryRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  summaryText: { fontSize: 13, fontFamily: FontFamily.sans, color: Colors.ink2, flex: 1 },
  footer: { flexDirection: 'row', padding: 14, gap: 10, backgroundColor: Colors.paper2 },
  btn: { flex: 1, height: 44, borderRadius: Radii.r4, justifyContent: 'center', alignItems: 'center' },
  btnPressed: { opacity: 0.85, transform: [{ scale: 0.98 }] },
  secondaryBtn: { backgroundColor: Colors.paper3 },
  secondaryText: { fontSize: 14, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans, color: Colors.ink2 },
  primaryText: { fontSize: 14, fontWeight: FontWeight.bold, fontFamily: FontFamily.sans, color: Colors.white },
  closeButton: {
    position: 'absolute', top: 14, right: 14, zIndex: 10,
    backgroundColor: Colors.paper2, borderRadius: Radii.full,
    width: 30, height: 30, justifyContent: 'center', alignItems: 'center',
  },
});
